import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, MapPin, Shield, Star, ExternalLink } from 'lucide-react';
import { COMPANY_INFO, SERVICE_AREAS, SERVICES } from '../../data/mock';

export const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#0f172a] text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">

          {/* Brand column */}
          <div className="lg:col-span-1">
            <img
              src="/header_new.webp"
              alt="AAA Irrigation Service"
              className="h-16 w-auto object-contain mb-4"
            />
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Integrity By The Drop Since 2005. TCEQ Licensed irrigation specialists serving Plano,
              Allen, Frisco and all of North Texas. Expert sprinkler repair and maintenance you can trust.
            </p>
            <a
              href={COMPANY_INFO.licenseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#059669]/20 border border-[#059669]/30 rounded-full px-3 py-1.5 text-xs text-[#059669] hover:bg-[#059669]/30 transition-colors"
            >
              <Shield className="w-3 h-3" />
              TCEQ License: {COMPANY_INFO.license}
            </a>
            <div className="flex items-center gap-1 mt-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              ))}
              <span className="text-slate-400 text-sm ml-1">4.9 · 29 Reviews</span>
            </div>
          </div>

          {/* Services column */}
          <div>
            <h3 className="text-white font-black uppercase tracking-widest text-xs mb-5">
              Our Services
            </h3>
            <ul className="space-y-2.5">
              {SERVICES.slice(0, 7).map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/services/${s.id}`}
                    className="text-sm text-slate-400 hover:text-[#1b7abf] transition-colors duration-200"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/services"
                  className="text-sm text-[#1b7abf] hover:text-blue-300 font-bold"
                >
                  View All Services →
                </Link>
              </li>
            </ul>
          </div>

          {/* Service Areas column */}
          <div>
            <h3 className="text-white font-black uppercase tracking-widest text-xs mb-5">
              Service Areas
            </h3>
            <ul className="grid grid-cols-2 gap-y-2 gap-x-2">
              {SERVICE_AREAS.map((a) => (
                <li key={a} className="text-sm text-slate-400 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-[#1b7abf] flex-shrink-0" />
                  {a}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact column */}
          <div>
            <h3 className="text-white font-black uppercase tracking-widest text-xs mb-5">
              Contact
            </h3>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${COMPANY_INFO.phone}`}
                  className="flex items-center gap-3 text-slate-300 hover:text-[#1b7abf] transition-colors group"
                >
                  <div className="w-9 h-9 bg-[#1b7abf]/20 rounded-lg flex items-center justify-center group-hover:bg-[#1b7abf]/40 transition-colors">
                    <Phone className="w-4 h-4 text-[#1b7abf]" />
                  </div>
                  <div>
                    <p className="font-bold text-white">{COMPANY_INFO.phoneFormatted}</p>
                    <p className="text-xs text-slate-500">Call or Text</p>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="flex items-center gap-3 text-slate-400 hover:text-[#1b7abf] transition-colors"
                >
                  <div className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center">
                    <Mail className="w-4 h-4 text-slate-400" />
                  </div>
                  <span className="text-sm">{COMPANY_INFO.email}</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400">
                  <div className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 text-slate-400" />
                  </div>
                  <div className="text-sm">
                    <p className="text-white font-medium">{COMPANY_INFO.hours.weekday}</p>
                    <p>{COMPANY_INFO.hours.saturday}</p>
                    <p className="text-[#ea580c] font-medium">{COMPANY_INFO.hours.sunday}</p>
                  </div>
                </div>
              </li>
              <li>
                <a
                  href="https://portal.aaairrigationservice.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-[#1b7abf] hover:text-blue-300 transition-colors text-sm font-bold"
                >
                  <ExternalLink className="w-4 h-4" />
                  Customer Portal Login
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Brand Partners strip */}
        <div className="border-t border-slate-800 py-8">
          <p className="text-center text-xs text-slate-600 uppercase tracking-widest mb-5">
            Authorized Service Provider &amp; Factory Certified
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8">
            {['Rain Bird', 'Hunter', 'Toro', 'Irritrol', 'Rachio', 'Irrigreen', 'Weathermatic'].map((b) => (
              <span
                key={b}
                className="text-slate-500 font-bold text-sm tracking-wide hover:text-slate-300 transition-colors cursor-default"
              >
                {b}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-slate-600">
            &copy; {year} AAA Irrigation Service LLC. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-600">
            <Link to="/privacy" className="hover:text-slate-400 transition-colors">Privacy Policy</Link>
            <span>&middot;</span>
            <Link to="/terms" className="hover:text-slate-400 transition-colors">Terms &amp; Conditions</Link>
          </div>
          <p className="text-xs text-slate-600 font-medium">Powered by Boatman Systems&trade;</p>
        </div>
      </div>
    </footer>
  );
};
